// src/pages/auth/AuthLayout.tsx
// Shared layout for all public auth pages — matches stitch design.

import { useState, useRef, useEffect } from 'react';
import { cn } from '@core/ui';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useTheme } from '@react/hooks';

interface AuthLayoutProps {
  children: React.ReactNode;
  className?: string;
}

function CordeLogo() {
  return (
    <svg className="size-6 text-white" fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z" fill="currentColor" />
    </svg>
  );
}

const LOCALE_LABELS: Record<string, string> = { en: 'English', ru: 'Русский', zh: '中文' };

export const AuthLayout = observer(({ children, className }: AuthLayoutProps) => {
  const { t, locale, setLocale, supportedLocales } = useI18n();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground selection:bg-primary/30 p-4">
      {/* Top-right controls: language + theme */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-1.5">
        {/* Language switcher (dropdown) */}
        <div ref={langRef} className="relative">
          <button
            onClick={() => setLangOpen((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-2 bg-card border border-border rounded-lg shadow-sm text-muted-foreground hover:text-foreground transition-colors text-xs font-medium"
          >
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
            {locale.toUpperCase()}
            <svg className={cn('size-3 transition-transform', langOpen && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {langOpen && (
            <div className="absolute right-0 mt-1 min-w-[140px] bg-card border border-border rounded-lg shadow-lg overflow-hidden">
              {supportedLocales.map((loc) => (
                <button
                  key={loc}
                  onClick={() => { setLocale(loc); setLangOpen(false); }}
                  className={cn(
                    'w-full px-3 py-2 text-left text-xs transition-colors flex items-center justify-between',
                    locale === loc
                      ? 'bg-primary/5 text-primary font-bold'
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  {LOCALE_LABELS[loc] ?? loc}
                  {locale === loc && (
                    <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 bg-card border border-border rounded-lg shadow-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('auth.toggleTheme')}
        >
          {isDark ? (
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
          ) : (
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
          )}
        </button>
      </div>

      {/* Decorative gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full blur-3xl bg-primary/[0.08]" />
        <div className="absolute top-[20%] -right-[5%] w-[30%] h-[30%] rounded-full blur-3xl bg-primary/[0.04]" />
      </div>

      <div className="relative flex flex-col items-center w-full">
        {/* Header / Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-primary p-2 rounded-xl shadow-lg shadow-primary/20">
              <CordeLogo />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">Corde</h1>
          </div>
          <p className="text-muted-foreground font-medium">{t('auth.tagline')}</p>
        </div>

        {/* Main content */}
        <div className={cn('w-full', className)}>
          {children}
        </div>

        {/* System status bar */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-emerald-500" />
            {t('auth.systemOperational')}
          </div>
          <div className="hidden sm:block w-px h-3 bg-border" />
          <Link to="#" className="hover:text-primary transition-colors">{t('auth.privacyPolicy')}</Link>
          <div className="hidden sm:block w-px h-3 bg-border" />
          <Link to="#" className="hover:text-primary transition-colors">{t('auth.termsOfService')}</Link>
        </div>
      </div>
    </div>
  );
});
